import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faWrench,
  faMagnifyingGlass,
  faListCheck,
} from '@fortawesome/free-solid-svg-icons';
import type {
  ProcedureExecutionState,
  ProcedureType,
} from '../../services/procedure-execution.service';

// Rendered on the dark section header — light translucent pill for contrast.
function ProcedureTypeBadge({ type }: { type: ProcedureType }) {
  const isService = type === 'SERVICE';
  return (
    <span className="flex items-center gap-1 rounded-full bg-primary-foreground/15 px-2 py-0.5 text-xs font-medium text-primary-foreground">
      <FontAwesomeIcon icon={isService ? faWrench : faMagnifyingGlass} className="h-2.5 w-2.5" />
      {isService ? 'Service' : 'Inspection'}
    </span>
  );
}

function computeMandatoryCounts(execution: ProcedureExecutionState): {
  completed: number;
  total: number;
} {
  const TERMINAL = new Set(['COMPLETED', 'SKIPPED', 'NOT_APPLICABLE']);
  let total = 0;
  let completed = 0;
  for (const sec of execution.sections) {
    for (const step of sec.steps) {
      if (step.isMandatory) {
        total++;
        if (TERMINAL.has(step.status)) completed++;
      }
    }
  }
  return { completed, total };
}

interface Props {
  execution: ProcedureExecutionState;
  isOpen: boolean;
  onToggle: () => void;
}

export function ExecutionPanelHeader({ execution, isOpen, onToggle }: Props) {
  const progress = parseFloat(execution.progressPercent);
  const progressDisplay = Math.round(progress);
  const isCompleted = execution.status === 'COMPLETED';
  const { completed: mandatoryCompleted, total: mandatoryTotal } =
    computeMandatoryCounts(execution);

  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-3 rounded-t-lg bg-primary px-5 py-3 text-left transition-colors hover:bg-primary/90 min-h-[52px]"
      aria-expanded={isOpen}
    >
      <FontAwesomeIcon icon={faListCheck} className="h-4 w-4 shrink-0 text-primary-foreground/85" />

      <div className="flex flex-1 min-w-0 flex-col gap-1">
        {/* Title row */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-primary-foreground truncate">
            {execution.procedureTitle}
            <span className="ml-1.5 font-mono font-normal text-primary-foreground/60 text-xs">
              v{execution.versionNumber}
            </span>
          </span>
          <ProcedureTypeBadge type={execution.procedureType} />
          {isCompleted && (
            <span className="flex items-center gap-1 rounded-full bg-green-500/25 px-2.5 py-0.5 text-xs font-medium text-green-100">
              <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" />
              Completed
            </span>
          )}
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-primary-foreground/75">
          {mandatoryTotal > 0 && (
            <span>
              Mandatory:{' '}
              <span
                className={`font-medium ${mandatoryCompleted === mandatoryTotal ? 'text-green-200' : 'text-primary-foreground'}`}
              >
                {mandatoryCompleted} / {mandatoryTotal}
              </span>
            </span>
          )}
          <span>
            Progress:{' '}
            <span className="font-medium text-primary-foreground">{progressDisplay}%</span>
          </span>
          <span>
            Status:{' '}
            <span className={`font-medium ${isCompleted ? 'text-green-200' : 'text-amber-200'}`}>
              {isCompleted ? 'Completed' : 'In Progress'}
            </span>
          </span>
        </div>

        {/* Progress bar */}
        {!isCompleted && (
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-primary-foreground/20">
            <div
              className="h-full rounded-full bg-primary-foreground transition-all duration-300 motion-reduce:transition-none"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              role="progressbar"
              aria-valuenow={progressDisplay}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        )}
      </div>

      <FontAwesomeIcon
        icon={isOpen ? faChevronDown : faChevronRight}
        className="h-4 w-4 shrink-0 text-primary-foreground/85 transition-transform"
      />
    </button>
  );
}
